<script>
import { mapActions, mapState } from 'vuex';
import { cloneDeep } from 'lodash-es';
import ItineraryEditPosterUploadImages from './ItineraryEditPosterUploadImages';

export default {
  name: 'ItineraryEditPosterImages',
  components: { ItineraryEditPosterUploadImages },
  props: {
    maxImages: {
      type: Number,
      default: 4,
      validator: value => value > 0,
    },
  },
  data() {
    return {
      localImages: [],
      expandedImage: null,
      formImages: [],
      selected: [],
      loadingImageId: null,
      uploadSuccess: false,
      draggedImage: null,
    };
  },
  computed: {
    ...mapState({
      selectedDomain: state => state.itinerary.domains.selected,
      images: state => state.itinerary.posterImages.items,
      loading: state => state.itinerary.posterImages.loading,
      error: state => state.itinerary.posterImages.error,
    }),
    selectedImages() {
      return this.localImages.filter(image => image.selected).slice(0, this.maxImages);
    },
    availableImages() {
      return this.localImages.filter(image => !image.selected);
    },
    canAddMoreImages() {
      return this.selectedImages.length < this.maxImages;
    },
  },
  watch: {
    images: {
      handler(newImages) {
        if (newImages) {
          this.localImages = cloneDeep(newImages);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.itineraryId = parseInt(this.$route.params.id, 10);

    this.getItineraryDomainPosterImages({
      itineraryId: this.itineraryId,
      domainId: this.selectedDomain.domainId,
    });
  },
  methods: {
    ...mapActions({
      getItineraryDomainPosterImages: 'itinerary/getItineraryDomainPosterImages',
      updateItineraryDomainPosterImages: 'itinerary/updateItineraryDomainPosterImages',
      uploadItineraryDomainPosterImages: 'itinerary/uploadItineraryDomainPosterImages',
    }),
    async uploadImages(event) {
      const formData = new FormData();
      event.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      try {
        await this.uploadItineraryDomainPosterImages({
          itineraryId: this.itineraryId,
          domainId: this.selectedDomain.domainId,
          formData,
        });
        this.uploadSuccess = true;
      } catch (error) {
        this.uploadSuccess = false;
      }
    },
    startDrag(event, draggedImage) {
      event.dataTransfer.setData('imageId', draggedImage.id);
    },
    onDropAdd(event, droppedImage) {
      if (!this.canAddMoreImages) return;
      const imageId = event.dataTransfer.getData('imageId');
      const draggedImage = this.images.find(image => image.id === imageId);
      const indexDraggedImage = this.selectedImages.indexOf(draggedImage);
      const indexDroppedImage = this.selectedImages.indexOf(droppedImage);
      let isMovingArray = false;
      if (this.availableImages.findIndex(image => image.id === imageId) >= 0) {
        isMovingArray = true;
      }
      const index = this.availableImages.findIndex(image => image.id === imageId);
      this.availableImages[index].selected = true;
      if (isMovingArray) {
        this.selectedImages.splice(indexDroppedImage + 1, 0, draggedImage);
        this.availableImages.splice(index, 1);
      } else if (indexDraggedImage < indexDroppedImage) {
        this.selectedImages.splice(indexDroppedImage + 1, 0, draggedImage);
        this.selectedImages.splice(indexDraggedImage, 1);
      } else {
        this.selectedImages.splice(indexDroppedImage, 0, draggedImage);
        this.selectedImages.splice(indexDraggedImage + 1, 1);
      }
      this.updateItineraryDomainPosterImages({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: this.selectedImages,
      });
    },
    onDropRemove(event) {
      const imageId = event.dataTransfer.getData('imageId');
      const draggedImage = this.images.find(image => image.id === imageId);
      const index = this.selectedImages.findIndex(image => image.id === imageId);
      this.selectedImages[index].selected = false;
      if (index < 0) {
        return;
      }
      this.availableImages.push(draggedImage);
      this.selectedImages.splice(index, 1);
      this.updateItineraryDomainPosterImages({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: this.selectedImages,
      });
    },
    moveImage(image, index, add) {
      if (add) {
        if (!this.canAddMoreImages) return;
        this.loadingImageId = image.id;
        const imageToAdd = { ...image, selected: true };
        this.selectedImages.push(imageToAdd);
        this.availableImages.splice(index, 1);
      } else {
        this.loadingImageId = image.id;
        const imageToRemove = { ...image, selected: false };
        this.availableImages.push(imageToRemove);
        this.selectedImages.splice(index, 1);
      }
      this.updateItineraryDomainPosterImages({
        itineraryId: this.itineraryId,
        domainId: this.selectedDomain.domainId,
        data: this.selectedImages,
      }).finally(() => {
        this.loadingImageId = null;
      });
    },
  },
};
</script>
<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h5 class="mb-0">Selected images:</h5>
        <small class="text-muted">{{ selectedImages.length }} of {{ maxImages }} images selected</small>
      </div>
    </div>
    <div
      v-if="!selectedImages.length"
      class="drag-and-drop"
      @drop="onDropAdd($event, null)"
      @dragover.prevent
      @dragenter.prevent
    >
      <p>Drag images here to add</p>
    </div>
    <div class="image-container">
      <div v-for="(image, index) in selectedImages" :key="`selected-${image.id}-${index}`" class="image-column">
        <div
          class="image-column__container"
          @drop="onDropAdd($event, image)"
          @dragover.prevent
          @dragenter.prevent
        >
          <div class="image-column__container--overlay">
            <v-icon
              :disabled="loading"
              class="image-column__button--danger"
              rotate="45"
              @click="moveImage(image, index, false)"
            >
              close
            </v-icon>
            <v-icon
              class="image-column__button"
              @click="expandedImage = image"
            >
              fullscreen
            </v-icon>
          </div>
          <div v-if="loadingImageId === image.id" class="image-column__container--loading">
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
          <v-img
            :src="image.small"
            draggable
            style="cursor: grab"
            class="image-column__container--img"
            alt="image"
            fluid
            @dragstart="startDrag($event, image)"
          />
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="mt-3">Available images:</h5>
    </div>

    <div class="image-container">
      <div v-for="(image, index) in availableImages" :key="`available-${image.id}-${index}`" class="image-column">
        <div
          :class="{ 'image-column__container--disabled': !canAddMoreImages }"
          class="image-column__container"
          @drop="onDropRemove($event, image)"
          @dragover.prevent
          @dragenter.prevent
        >
          <div class="image-column__container--overlay">
            <v-icon
              :disabled="loading"
              class="image-column__button--success"
              @click="canAddMoreImages && moveImage(image, index, true)"
            >
              add
            </v-icon>
            <v-icon
              class="image-column__button"
              @click="expandedImage = image"
            >
              fullscreen
            </v-icon>
          </div>
          <div v-if="loadingImageId === image.id" class="image-column__container--loading">
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
          <v-img
            :src="image.small"
            :draggable="canAddMoreImages"
            :class="{ 'cursor-grab': canAddMoreImages, 'cursor-not-allowed': !canAddMoreImages }"
            class="image-column__container--img"
            alt="image"
            fluid
            @dragstart="canAddMoreImages && startDrag($event, image)"
          />
        </div>
        <p>{{ image.entityName }}</p>
      </div>
    </div>
    <div v-if="expandedImage" class="fullscreen-overlay" @click="expandedImage = null">
      <div class="fullscreen-overlay__container">
        <v-icon
          class="fullscreen-overlay__close-button"
          medium
          @click="expandedImage = null"
        >close</v-icon>
        <v-img :src="expandedImage.large" fluid class="fullscreen-overlay__img"/>
      </div>
    </div>

    <itinerary-edit-poster-upload-images
      v-if="canAddMoreImages"
      :images="localImages"
      :max-images="maxImages"
      :upload-success="uploadSuccess"
      @upload-images="uploadImages"
    />
  </div>
</template>

<style scoped lang="scss">
.button {
  height: 32px;
  width: 32px;
  color: black;
  background: white;
  border: white;
  z-index: 15;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-and-drop {
  min-height: 100px;
  background-color: whitesmoke;
  opacity: 70%;
  text-align: center;
  outline-style: dashed;
  outline-width: 1px;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  & p {
   margin: 0;
    user-select: none;
  }
}
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  height: 100dvh;
  width: 100%;
  background-color: rgba(0,0,0,0.5);
  cursor: pointer;
  padding: 48px;
  &__container {
    position: relative;
    width: 100%;
    height: 100%;
  }
  &__close-button {
    color: black;
    position: absolute;
    right: 16px;
    top: 16px;
    width: fit-content;
    transition: opacity .35s ease;
    z-index:1000;
    cursor: pointer;
    background-color: white;
    border-radius: 50%;
    padding: 4px;
    box-shadow: rgba(0, 0, 0, 0.2) 0 4px 8px, rgba(0, 0, 0, 0.1) 0 1px 3px;
    &:hover {
      transform-origin: center;
      transform:scale(1.025);
    }
  }
  &__img {
    width: 100%;
    height: 100%;
    background-size: cover;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
  }
}

.img-button, .img-button-danger {
  width: 50%;
  height: 100%;
  color: white;
  border: white;
  z-index: 15;
}

.img-button-danger {
  background-color: #ed5565;
  border-color: #ed5565;
}

.image-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: -16px;
  margin-left: -16px;
}

.image-column {
  flex: 0 0 calc(100% / 8);
  max-width: calc(100% / 8);
  padding-right: 16px;
  padding-left: 16px;
  margin-bottom: 12px;

  &:hover &__container--overlay {
    opacity: 1;
  }

  &__button {
    @extend .button;

    &--success {
      @extend .button;
      background-color: #1ab394;
      border-color: #1ab394;
      color: white;
    }

    &--danger {
      @extend .button;
      background-color: #ed5565;
      border-color: #ed5565;
      color: white;
    }
  }

  &__container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 4/3;
    width: 100%;

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;

      .image-column__container--overlay {
        opacity: 0 !important;
      }

      .image-column__container--img {
        cursor: pointer;
      }

      &:hover {
        opacity: 0.5;
      }
    }

    &--overlay {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      width: 100%;
      height: 100%;
      background-color: rgba(0,0,0,0.25);
      position: absolute;
      right: 0;
      top: 0;
      z-index: 10;
      opacity: 0;
      cursor: pointer;
      transition: opacity .35s ease;
    }

    &--img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.7);
      z-index: 20;
    }
  }

  & p {
    margin-top: 4px;
    text-align: center;
    margin-bottom: 0;
  }
}

.cursor-grab {
  cursor: grab;
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryEditPosterImages.vue